import React, {
  MouseEvent,
  useState,
} from 'react';
import IconButton from '@mui/material/IconButton';
import MailIcon from '@mui/icons-material/MailOutlineRounded';
import PrinterIcon from 'core/icons/printer';
import ToolTip from 'core/components/Tooltip';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  PatientPaymentTransaction,
} from 'pages/Dashboard/pages/Billing/types';
import DeleteIcon from 'core/icons/delete';
import {
  useDeletePayment,
} from 'pages/Dashboard/pages/Billing/hooks';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import {
  PatientPaymentTransactionType,
} from 'dtos/patientPaymentTransactionType';
import ReceiptEmailDialog from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/ReceiptEmailDialog';

export default function RowActions(payment: PatientPaymentTransaction = {}) {
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const { confirm, closeConfirm } = useConfirmDialog();
  const notify = useNotify();

  const handlePrintInvoice = async (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setIsPrinting(true);
    paymentsEventChannel.emit('onPaymentPrint', payment, () => { setIsPrinting(false); });
  };

  const {
    mutateAsync: deletePayment,
    isPending: isDeletingPayment,
  } = useDeletePayment();

  const onDeletePayment = async () => {
    const response = await deletePayment({ id: payment.patientPaymentTransactionId });
    notify({
      message: response?.displayMessage ?? 'Payment is deleted successfully.',
      variant: 'success',
    });
    paymentsEventChannel.emit('onPaymentsDataChanged', 'patient-payments');
  };

  const handleDeletePayment = async (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    confirm({
      title: 'Delete Payment',
      content: 'Are you sure you want to delete this payment?',
      onConfirm: async () => {
        try {
          await onDeletePayment();
        } catch (error) {
          console.error(error);
        } finally {
          closeConfirm();
        }
      },
    });
  };

  const isRefund = payment.type === PatientPaymentTransactionType.Refund;
  const disabledTooltip = isRefund
    ? 'Refunds cannot be deleted'
    : 'Square transactions cannot be deleted';

  return (
    <>
      <div className="flex justify-start w-full">
        <ToolTip title="Print Receipt">
          <IconButton
            aria-label="Print Receipt"
            onClick={handlePrintInvoice}
            disabled={isPrinting}
            className="!h-8 !w-8 disabled:opacity-50 flex items-center justify-center"
          >
            <PrinterIcon className="!w-4 !h-4 !text-black" />
          </IconButton>
        </ToolTip>
        <ToolTip title="Email Receipt">
          <IconButton
            aria-label="Email Receipt"
            onClick={(e) => {
              e.stopPropagation();
              setEmailDialogOpen(true);
            }}
            className="!h-8 !w-8 flex items-center justify-center"
          >
            <MailIcon className="!w-4 !h-4 !text-black" />
          </IconButton>
        </ToolTip>
        <ToolTip title={(payment.isSquareTransaction || isRefund)
          ? disabledTooltip
          : 'Delete Payment'}
        >
          <span>
            <IconButton
              aria-label="Cancel Payment"
              onClick={handleDeletePayment}
              disabled={isDeletingPayment || isRefund || payment.isSquareTransaction}
              className="!h-8 !w-8 disabled:opacity-50 flex items-center justify-center"
              color="error"
            >
              {isDeletingPayment
                ? <Spinner size={16} />
                : <DeleteIcon className="!h-5 !w-5 !text-black" />}
            </IconButton>
          </span>
        </ToolTip>
      </div>
      {emailDialogOpen && (
        <ReceiptEmailDialog
          payment={payment}
          onClose={() => { setEmailDialogOpen(false); }}
        />
      )}
    </>
  );
}
